/** Context describes an expense's role, independently of its merchant/category.
 * Suggestions never alter accounting totals or claim to identify fraud.
 */
export const expenseContexts = {
  commitment: { label: 'Recurring commitment', description: 'A regular bill or subscription you plan around.' },
  everyday: { label: 'Everyday spending', description: 'Routine spending that can vary from week to week.' },
  occasional: { label: 'Occasional cost', description: 'An irregular cost, such as a repair or annual purchase.' },
  movement: { label: 'Money movement', description: 'A transfer, repayment, or savings contribution.' },
  unclassified: { label: 'Needs review', description: 'There is not enough information to describe this expense yet.' }
} as const;
export type ExpenseContext = keyof typeof expenseContexts;
export interface ContextInput {
  contextOverride?: string;
  category?: string;
  frequency?: string;
}
export function isExpenseContext(value: unknown): value is ExpenseContext {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(expenseContexts, value);
}
export function describeExpense(input: ContextInput): {
  kind: ExpenseContext; source: 'user' | 'suggested' | 'unknown'; reason: string;
} {
  if (isExpenseContext(input.contextOverride)) {
    return { kind: input.contextOverride, source: 'user', reason: 'Chosen by you for this tracked expense.' };
  }
  const category = (input.category || '').trim().toLowerCase();
  // A category is evidence for a suggestion, not proof of a matched transfer.
  if (['savings', 'emergency fund', 'investment', 'debt payment', 'credit card', 'transfer', 'transfers'].includes(category)) {
    return { kind: 'movement', source: 'suggested', reason: 'This category may describe a transfer or repayment. Check before treating it differently from spending.' };
  }
  if (['weekly', 'bi-weekly', 'monthly', 'yearly'].includes(input.frequency || '')) {
    return { kind: 'commitment', source: 'suggested', reason: 'This expense has a recurring schedule. You can change its context if that does not describe it.' };
  }
  if (['food & dining', 'groceries', 'transportation', 'personal care'].includes(category)) {
    return { kind: 'everyday', source: 'suggested', reason: 'This category often includes routine spending. A one-off purchase may belong elsewhere.' };
  }
  // One-time is a recurrence setting, not sufficient evidence of an exceptional cost.
  return { kind: 'unclassified', source: 'unknown', reason: 'A merchant, amount, or one-time date alone does not tell the whole story.' };
}

export type ExpenseContextFilter = 'all' | 'review' | ExpenseContext;
export function matchesExpenseContext(input: ContextInput, filter: ExpenseContextFilter): boolean {
  if (filter === 'all') return true;
  const context = describeExpense(input);
  if (filter === 'review') return context.source !== 'user' || context.kind === 'unclassified';
  return context.kind === filter;
}
